// The month grid every scheduling screen draws: Monday-first, leading blanks,
// and each day flagged in or out of the bookable window. Shared by the Contact
// Center's confirm panel and the ambassador's "Reagendar" picker so both agree
// on what a week looks like.

const pad = (n: number): string => String(n).padStart(2, "0");

/** Local "YYYY-MM-DD" for a Date (no UTC shift — the agenda is a local day). */
export const toISODate = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const todayISODate = (): string => toISODate(new Date());

/** How far ahead a delivery can be scheduled. */
export const SCHEDULE_WINDOW_DAYS = 33;

/** The last schedulable date (today + window), as "YYYY-MM-DD". */
export const maxISODate = (todayISO: string, windowDays = SCHEDULE_WINDOW_DAYS): string => {
  const [y, m, d] = todayISO.split("-").map(Number);
  const dt = new Date(y, m - 1, d + windowDays);
  return toISODate(dt);
};

/** Combines a date ("YYYY-MM-DD") + exact time ("HH:MM") into an ISO instant. */
import { gtInstant } from "./slots";

export const slotISO = (date: string, time: string): string =>
  gtInstant(date, time).toISOString();

/** A "YYYY-MM-DD" back to a local Date at midnight. */
export const fromISODate = (iso: string): Date => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

const DAYS_ES = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
export const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
/** Weekday header letters, Monday-first (to match the mock). */
export const WEEK_LETTERS = ["L", "M", "M", "J", "V", "S", "D"];

/** Long form of a date ("miércoles 29 de julio"). */
export const fechaLarga = (iso: string): string => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return "";
  const dt = new Date(y, m - 1, d);
  return `${DAYS_ES[dt.getDay()]} ${d} de ${MONTHS_ES[m - 1]}`;
};

export const monthLabel = (year: number, month: number): string =>
  `${MONTHS_ES[month]} ${year}`;

export interface CalendarCell {
  /** "YYYY-MM-DD", or "" for a leading blank cell. */
  iso: string;
  /** Day-of-month label, or "" for a blank cell. */
  label: string;
  blank: boolean;
  disabled: boolean;
  isToday: boolean;
}

/** A month grid (Monday-first), each real day flagged in/out of the [min,max]
 *  schedulable window. Leading blanks pad the first week. */
export const buildMonth = (
  year: number,
  month: number,
  minISO: string,
  maxISO: string,
  todayISO: string,
): CalendarCell[] => {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7; // Monday-first offset
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: CalendarCell[] = [];
  for (let i = 0; i < lead; i++) {
    cells.push({ iso: "", label: "", blank: true, disabled: true, isToday: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
    cells.push({
      iso,
      label: String(day),
      blank: false,
      disabled: iso < minISO || iso > maxISO,
      isToday: iso === todayISO,
    });
  }
  return cells;
};

/** Whether the month before (year,month) still holds a schedulable day. */
export const canGoPrev = (year: number, month: number, minISO: string): boolean => {
  const [minY, minM] = minISO.split("-").map(Number);
  return year > minY || (year === minY && month > minM - 1);
};

/** Whether the month after (year,month) still holds a schedulable day. */
export const canGoNext = (year: number, month: number, maxISO: string): boolean => {
  const [maxY, maxM] = maxISO.split("-").map(Number);
  return year < maxY || (year === maxY && month < maxM - 1);
};
